import {
  calculateEndHourAndLabel,
  convertTo12Hour,
  convertTo24HourString,
} from 'features/common/DateTimeSelector/utils';
import {
  DAYS_OF_WEEK,
  DayOfWeek,
  HOURS_IN_DAY,
  MIN_IN_HOUR,
  formatTimeOfDay12HrClock,
} from 'features/common/datetimeUtils';

const MIN_CLOSURE_HOURS = 1;
export const MAX_CLOSURE_HOURS = 60;
export const MAX_CLOSURE_DAYS = 4;

function dayIndex(day: DayOfWeek): number {
  return DAYS_OF_WEEK.indexOf(day);
}

function timeToHours(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

export function resolveEndTimeForEndDayChange(
  startDay: DayOfWeek,
  endDay: DayOfWeek,
  closureStart: string,
  closureEnd: string,
): { closureEnd: string; durationHours: number } {
  const startH = timeToHours(closureStart);
  const endH = timeToHours(closureEnd);

  // case : same day, closureEnd < closureStart (auto correct) - while selecting end day
  if (startDay === endDay && endH <= startH) {
    const durationHours = MIN_CLOSURE_HOURS;
    const { endHour } = calculateEndHourAndLabel(
      convertTo12Hour(closureStart),
      durationHours,
    );
    return {
      closureEnd: convertTo24HourString(endHour),
      durationHours,
    };
  }

  // regular case
  const duration = closureDurationHours(
    startDay,
    endDay,
    closureStart,
    closureEnd,
  );

  if (duration <= MAX_CLOSURE_HOURS) {
    return { closureEnd, durationHours: duration };
  }
  // case : endDay = max day, duration > MAX_CLOSURE_HOURS; default if something goes wrong
  const startHour12 = convertTo12Hour(closureStart);
  const { endHour: maxEndHour12 } = calculateEndHourAndLabel(
    startHour12,
    MAX_CLOSURE_HOURS,
  );
  const maxEnd24 = convertTo24HourString(maxEndHour12);

  return { closureEnd: maxEnd24, durationHours: MAX_CLOSURE_HOURS };
}

export function inferEndDayFromDuration(
  startDay: DayOfWeek,
  startHour12: string,
  durationHours: number,
): DayOfWeek {
  const { daysRolled } = calculateEndHourAndLabel(startHour12, durationHours);
  const startIdx = dayIndex(startDay);
  return DAYS_OF_WEEK[(startIdx + Math.floor(daysRolled)) % 7];
}

export function getValidEndDays(
  start: DayOfWeek,
  closureStart?: string,
): DayOfWeek[] {
  let dayCount = MAX_CLOSURE_DAYS;
  if (closureStart) {
    const maxEndDay = inferEndDayFromDuration(
      start,
      convertTo12Hour(closureStart),
      MAX_CLOSURE_HOURS,
    );
    dayCount = getDayDiff(start, maxEndDay).dayDiff + 1;
  }
  return Array.from(
    { length: dayCount },
    (_, idx) => DAYS_OF_WEEK[(dayIndex(start) + idx) % 7],
  );
}

const getDayDiff = (start: DayOfWeek, end: DayOfWeek) => {
  const startIdx = dayIndex(start);
  const endIdx = dayIndex(end);
  return { dayDiff: (endIdx - startIdx + 7) % 7, endIdx, startIdx };
};

const expandDaysOfWeek = (start: DayOfWeek, end: DayOfWeek): DayOfWeek[] => {
  const { dayDiff, startIdx } = getDayDiff(start, end);
  return Array.from(
    { length: dayDiff + 1 },
    (_, k) => DAYS_OF_WEEK[(startIdx + k) % 7],
  );
};

export function normalizeDaysOfWeeks(days: DayOfWeek[]): DayOfWeek[] {
  if (days.length <= 1) return days;
  if (days[0] === days[1]) return [days[0]];
  return expandDaysOfWeek(days[0], days.at(-1));
}

export function closureDurationHours(
  startDay: DayOfWeek,
  endDay: DayOfWeek,
  closureStart: string,
  closureEnd: string,
): number {
  const startH = timeToHours(closureStart);
  let endH = timeToHours(closureEnd);
  if (startDay === endDay) {
    if (endH <= startH) endH += HOURS_IN_DAY;
    return endH - startH;
  }
  const { dayDiff } = getDayDiff(startDay, endDay);
  return dayDiff * HOURS_IN_DAY + (endH - startH);
}

export type ClosureTimeslot = {
  dayName: DayOfWeek;
  minutesOfDay: number;
  minutesFromStart: number;
  slotIndex: number;
};

/** Mirrors backend get_closure_time_period_times_and_indexes. */
export function getClosureTimeslots(
  closureStart: string | undefined,
  closureEnd: string | undefined,
  binSize: number,
  startDay: DayOfWeek,
  endDay: DayOfWeek,
): ClosureTimeslot[] {
  const days = normalizeDaysOfWeeks([startDay, endDay]);
  if (!days.length || !closureStart || !closureEnd || !binSize) {
    return [];
  }

  const durationMin =
    closureDurationHours(startDay, endDay, closureStart, closureEnd) * 60;

  if (durationMin <= 0 || !Number.isFinite(durationMin)) {
    return [];
  }

  const startMins = Math.round(timeToHours(closureStart) * 60);
  if (!Number.isFinite(startMins)) return [];

  const minsPerDay = HOURS_IN_DAY * 60;
  const slots: ClosureTimeslot[] = [];
  let dayIndex = 0;
  let currentMins = startMins;
  let elapsed = 0;
  let slotIndex = 0;

  // 11:00am metrics cover the 11:00am-11:15am time slot.
  // The last time slider slot should show the same metrics as the previous slot.
  // For example, for a 15 min closure - the 11:15pm-11:30pm Friday slot,
  // do not show a separate 11:30pm-11:45pm metrics slot
  while (elapsed <= durationMin) {
    const dayName = days[Math.min(dayIndex, days.length - 1)];

    if (elapsed === durationMin) {
      const lastSlot = slots.at(-1);
      if (
        lastSlot &&
        lastSlot.minutesOfDay === currentMins &&
        lastSlot.dayName === dayName
      ) {
        break;
      }
      slots.push({
        dayName,
        minutesOfDay: currentMins,
        minutesFromStart: durationMin,
        slotIndex: lastSlot?.slotIndex ?? slotIndex,
      });
      break;
    }

    slots.push({
      dayName,
      minutesOfDay: currentMins,
      minutesFromStart: elapsed,
      slotIndex,
    });

    elapsed += binSize;
    const nextMins = currentMins + binSize;
    if (nextMins >= minsPerDay) {
      dayIndex += 1;
      currentMins = nextMins - minsPerDay;
    } else {
      currentMins = nextMins;
    }
    slotIndex += 1;
  }
  return slots;
}

export function closureDayNames(timeslots: ClosureTimeslot[]) {
  return [...new Set(timeslots.map((slot) => slot.dayName))];
}

export function closureTimeslotIndex(
  timeslots: ClosureTimeslot[],
  dayName: DayOfWeek,
  time: string,
): number {
  const minutesOfDay = Math.round(timeToHours(time) * MIN_IN_HOUR);
  const slot = timeslots.find(
    (s) => s.dayName === dayName && s.minutesOfDay === minutesOfDay,
  );
  return slot?.slotIndex ?? -1;
}

export function formatClosureOffsetLabel(
  sliderSlotIndex: number,
  timeslots: ClosureTimeslot[],
): string {
  if (!timeslots.length) return formatTimeOfDay12HrClock(0);
  const slot = timeslots[Math.min(sliderSlotIndex, timeslots.length - 1)];
  return `${slot.dayName.slice(0, 3)}, ${formatTimeOfDay12HrClock(slot.minutesOfDay)}`;
}

export function formatClosureDurationLabel(
  closureStart: string | undefined,
  closureEnd: string | undefined,
  startDay: string | undefined,
  endDay: string | undefined,
): string | undefined {
  if (!closureStart || !closureEnd || !startDay || !endDay) return undefined;
  return `${startDay.slice(0, 3)}, ${convertTo12Hour(closureStart)} to ${endDay.slice(0, 3)}, ${convertTo12Hour(closureEnd)}`;
}

export function validateClosureDuration(
  startDay: DayOfWeek,
  endDay: DayOfWeek,
  closureStart: string,
  closureEnd: string,
): string | undefined {
  const duration = closureDurationHours(
    startDay,
    endDay,
    closureStart,
    closureEnd,
  );
  if (duration > MAX_CLOSURE_HOURS)
    return `Closure cannot exceed ${MAX_CLOSURE_HOURS} hours`;
}
